import {
  AdobeDataLayerEvent,
  Product,
  AdobeProduct,
  ComponentData,
  PageViewEvent,
  AdobeEventType,
} from '@types';
import { SiteConfig } from './SiteConfig';
import {
  convertCurrencyToCode, isCartPageAlreadyLoaded, transformToAdobeProduct, isPageViewAlreadyFired,
  getUserLoginStatus,
  detectPlatform,
} from './adoberHelper';

const COMPONENT_CONFIGS = {
  promoCode: {
    componentType: 'button',
    componentText: 'apply promo code',
    clickCategory: 'cta',
    parentComponentType: 'promo code section',
    parentComponentText: 'promo code input',
  },
  removeButton: {
    componentType: 'button',
    componentText: 'remove from cart',
    clickCategory: 'cta',
    parentComponentType: 'product list',
    parentComponentText: 'cart products',
  },
  checkoutButton: {
    componentType: 'button',
    componentText: 'proceed to checkout',
    clickCategory: 'cta',
    parentComponentType: 'order summary',
    parentComponentText: 'cart summary',
  },
  continueShoppingButton: {
    componentType: 'button',
    componentText: 'continue shopping',
    clickCategory: 'cta',
    parentComponentType: 'order summary',
    parentComponentText: 'cart summary',
  },
};

const createBaseEvent = (
  eventName: string,
  additionalData: Partial<AdobeDataLayerEvent> = {},
): AdobeDataLayerEvent => ({
  event: eventName,
  ...additionalData,
});

const pushEvent = (eventData: AdobeDataLayerEvent): void => {
  window.adobeDataLayer.push(eventData);
};

const createComponentData = (
  configKey: keyof typeof COMPONENT_CONFIGS,
  overrides: Partial<ComponentData> = {},
): ComponentData => ({
  ...COMPONENT_CONFIGS[configKey],
  componentProductCode: '',
  componentPosition: '',
  linkURL: '',
  ...overrides,
});

export const onPageView = (): void => {
  if (isPageViewAlreadyFired()) return;

  const {
    lang, iso: countryName,
  } = SiteConfig.getConfig();
  const {
    loginStatus, loginType, adminType, almId, connectId,
  } = getUserLoginStatus();

  const { hostname } = window.location;
  const localeStr = `${countryName.toLowerCase()}-${lang.toLowerCase()}`;

  const eventData: PageViewEvent = {
    event: AdobeEventType.PAGE_VIEW,
    site: {
      name: hostname,
      platform: detectPlatform(),
      locale: localeStr,
    },
    page: {
      pageName: 'cart',
      section: 'cart',
      subSection: '',
      subSubSection: '',
      pageCategory: 'ecommerce',
      pageLanguage: lang.toLowerCase(),
      pageTemplate: 'cart-page',
      pageBuild: 'react',
      pageNumber: '',
      breadcrumb: 'home > cart',
    },
    user: {
      loginStatus,
      loginType,
      adminType,
      almId,
      connectId,
    },
  };

  window.adobeDataLayer.push(eventData);
};

export const onCartPageLoad = (productList: Product[], currencyValue: string) => {
  if (isCartPageAlreadyLoaded()) return;

  const adobeProducts: AdobeProduct[] = productList.map(transformToAdobeProduct);

  const eventData = createBaseEvent(AdobeEventType.CART_VIEW, {
    ecommerce: {
      currency: convertCurrencyToCode(currencyValue),
      products: adobeProducts,
    },
  });

  pushEvent(eventData);
};

export const onApplyPromoSuccess = (promo: string | undefined) => {
  const eventData = createBaseEvent(AdobeEventType.PROMO_CODE_SUCCESS, {
    promoCode: promo?.toLowerCase(),
    component: createComponentData('promoCode'),
  });

  pushEvent(eventData);
};

export const onApplyPromoError = (promo: string) => {
  const eventData = createBaseEvent(AdobeEventType.PROMO_CODE_FAILURE, {
    promoCode: promo?.toLowerCase(),
    error: {
      errorMessage: `sorry, no discounts were found for ${promo}`,
    },
    component: createComponentData('promoCode'),
  });

  pushEvent(eventData);
};

export const onRemoveBtnClick = (product: Product, currencyValue: string) => {
  const adobeRemovedData = transformToAdobeProduct(product);

  const eventData = createBaseEvent(AdobeEventType.REMOVE_FROM_CART, {
    ecommerce: {
      currency: convertCurrencyToCode(currencyValue),
      products: [adobeRemovedData],
    },
    component: createComponentData('removeButton'),
  });

  pushEvent(eventData);
};

export const onCheckoutBtnClick = (productList: Product[], currencyValue: string, promo?: string) => {
  const productDataList: AdobeProduct[] = productList.map(transformToAdobeProduct);

  const eventData = createBaseEvent(AdobeEventType.PROCEED_TO_CHECKOUT, {
    ecommerce: {
      currency: convertCurrencyToCode(currencyValue),
      products: productDataList,
    },
    component: createComponentData('checkoutButton'),
    ...(promo ? { promoCode: promo } : {}),
  });

  pushEvent(eventData);
};

export const onContinueShoppingClick = (context: 'emptyCart' | 'orderSummary' = 'orderSummary'): void => {
  const componentOverrides = context === 'emptyCart' ? {
    parentComponentType: 'empty cart',
    parentComponentText: 'empty cart message',
  } : {};

  const eventData = createBaseEvent(AdobeEventType.CLICK, {
    component: createComponentData('continueShoppingButton', componentOverrides),
  });

  pushEvent(eventData);
};
